import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip as MuiTooltip } from '@mui/material'; // Alias to avoid naming conflict
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { mediumGray, teal2 } from '../../../common/theme/color-constants';
import { Languages } from '../../../common/v2/constants';
import { useGlobalLocaleContext } from '../../../common/v2/context/contexts';

interface MetricDataForTooltip {
  tooltipJapanese: string | null;
  tooltipEnglish: string | null;
  metricCategoryName: string;
  metricGroupName: string;
  metricName: string;
}

interface OpenMetricTooltipIconProps {
  metric: MetricDataForTooltip;
  onOpen: (
    event: React.MouseEvent<HTMLElement>,
    popoverData: MetricDataForTooltip
  ) => void;
}

const StyledTableInfoIcon = styled(InfoOutlinedIcon)<{ $isActive: boolean }>`
  font-size: 1rem;
  margin-left: 4px;
  color: ${({ $isActive }) => ($isActive ? teal2 : mediumGray)};
  cursor: ${({ $isActive }) => ($isActive ? 'pointer' : 'default')};
  vertical-align: middle;

  &:hover {
    opacity: ${({ $isActive }) => ($isActive ? 0.7 : 1)};
  }
`;

export const OpenMetricTooltipIcon: React.FC<OpenMetricTooltipIconProps> = ({
  metric,
  onOpen,
}) => {
  const { selected: selectedLanguage } = useGlobalLocaleContext();
  const { t } = useTranslation();
  const currentUiLanguage = selectedLanguage;

  const isTooltipAvailable = (): boolean => {
    return (
      match(currentUiLanguage)
        .with(Languages.JA, () => metric.tooltipJapanese)
        .with(Languages.EN, () => metric.tooltipEnglish)
        .exhaustive() !== null
    );
  };

  const isActive = isTooltipAvailable();

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (isActive) {
      onOpen(event as unknown as React.MouseEvent<HTMLElement>, metric);
    }
  };

  const tooltipTitle = isActive
    ? t('pages:metrics.metricTooltip.showDetails')
    : t('pages:metrics.metricTooltip.noDetails');

  return (
    <MuiTooltip title={tooltipTitle} placement="top">
      {/* The span is necessary for MuiTooltip to work correctly with disabled/non-interactive child when isActive is false */}
      <span>
        <StyledTableInfoIcon $isActive={isActive} onClick={handleClick} />
      </span>
    </MuiTooltip>
  );
};
